/**
 * analytics.js — single trackEvent() layer for GA4, Meta Pixel, TikTok Pixel and
 * Microsoft Clarity, plus first-party forwarding to /api/analytics/event (which
 * relays Purchase/InitiateCheckout/etc. to Meta CAPI + TikTok Events API with the
 * same event_id so browser + server events de-duplicate).
 *
 * Third-party pixels only load after cookie consent ("accepted" in
 * stagvault_cookie_consent_v1). First-party events carry no PII and always fire.
 *
 * Env (frontend/.env, all optional): REACT_APP_GA4_ID, REACT_APP_META_PIXEL_ID,
 * REACT_APP_TIKTOK_PIXEL_ID, REACT_APP_CLARITY_ID.
 */
import { api } from "./api";
import { readStickyUtm } from "../hooks/useUtmParams";

const CONSENT_KEY = "stagvault_cookie_consent_v1";
const CLICK_KEY = "stagvault_click_ids";
const VISITOR_KEY = "stagvault_visitor_id";

export function visitorId() {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) { v = (window.crypto?.randomUUID?.() || `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`).replace(/-/g, ""); localStorage.setItem(VISITOR_KEY, v); }
    return v;
  } catch { return null; }
}
// IDs: admin-saved values from /api/analytics/tracking-config win; .env values are the fallback.
const IDS = {
  ga4: process.env.REACT_APP_GA4_ID || "",
  meta: process.env.REACT_APP_META_PIXEL_ID || "",
  tiktok: process.env.REACT_APP_TIKTOK_PIXEL_ID || "",
  clarity: process.env.REACT_APP_CLARITY_ID || "",
};
let configLoaded = false;

async function loadRemoteConfig() {
  if (configLoaded) return;
  configLoaded = true;
  try {
    const { data } = await api.get("/analytics/tracking-config");
    if (data?.ga4_id) IDS.ga4 = data.ga4_id;
    if (data?.meta_pixel_id) IDS.meta = data.meta_pixel_id;
    if (data?.tiktok_pixel_id) IDS.tiktok = data.tiktok_pixel_id;
    if (data?.clarity_id) IDS.clarity = data.clarity_id;
  } catch { /* fall back to env IDs */ }
}

// internal name → [ga4 event, meta event (std or custom), tiktok std event or null]
export const EVENT_MAP = {
  LandingPageView: ["landing_page_view", "LandingPageView", null],
  TrackViewed: ["view_item", "ViewContent", "ViewContent"],
  PreviewStarted: ["preview_started", "PreviewStarted", null],
  PromptRun: ["prompt_run", "PromptRun", null],
  QuizCompleted: ["quiz_completed", "QuizCompleted", null],
  EmailCaptured: ["generate_lead", "Lead", "SubmitForm"],
  PricingViewed: ["view_item_list", "PricingViewed", null],
  CheckoutStarted: ["begin_checkout", "InitiateCheckout", "InitiateCheckout"],
  Purchase: ["purchase", "Purchase", "CompletePayment"],
  ProductPurchased: ["product_purchased", "ProductPurchased", null],
};
const META_STANDARD = new Set(["PageView", "ViewContent", "Lead", "CompleteRegistration", "InitiateCheckout", "Purchase"]);

let loaded = false;

export function hasConsent() {
  try { return localStorage.getItem(CONSENT_KEY) === "accepted"; } catch { return false; }
}

export function newEventId(prefix = "ev") {
  const rnd = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}_${rnd}`;
}

function injectScript(src, onload) {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  if (onload) s.onload = onload;
  document.head.appendChild(s);
}

export function captureClickIds() {
  if (typeof window === "undefined") return;
  try {
    const p = new URLSearchParams(window.location.search);
    const stored = JSON.parse(localStorage.getItem(CLICK_KEY) || "{}");
    const now = Date.now();
    let changed = false;
    const fbclid = p.get("fbclid");
    if (fbclid) { stored.fbc = `fb.1.${now}.${fbclid}`; changed = true; }
    const ttclid = p.get("ttclid");
    if (ttclid) { stored.ttclid = ttclid; changed = true; }
    const gclid = p.get("gclid");
    if (gclid) { stored.gclid = gclid; changed = true; }
    if (!stored.landing_page) { stored.landing_page = window.location.pathname + window.location.search; changed = true; }
    if (changed) localStorage.setItem(CLICK_KEY, JSON.stringify(stored));
  } catch { /* storage blocked */ }
}

function cookie(name) {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

export function attribution() {
  let click = {};
  try { click = JSON.parse(localStorage.getItem(CLICK_KEY) || "{}"); } catch { /* noop */ }
  return {
    utm_source: readStickyUtm("utm_source") || null,
    utm_medium: readStickyUtm("utm_medium") || null,
    utm_campaign: readStickyUtm("utm_campaign") || null,
    utm_content: readStickyUtm("utm_content") || null,
    utm_term: readStickyUtm("utm_term") || null,
    fbp: cookie("_fbp"),
    fbc: cookie("_fbc") || click.fbc || null,
    ttp: cookie("_ttp"),
    ttclid: click.ttclid || null,
    gclid: click.gclid || null,
    landing_page: click.landing_page || null,
  };
}

export async function loadPixels() {
  if (loaded || typeof window === "undefined" || !hasConsent()) return;
  await loadRemoteConfig();
  if (loaded) return;
  if (!IDS.ga4 && !IDS.meta && !IDS.tiktok && !IDS.clarity) return;
  loaded = true;
  if (IDS.ga4) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", IDS.ga4, { send_page_view: true, linker: { domains: ["stagvault.co.uk", "shop.stagvault.co.uk"], accept_incoming: true } });
    injectScript(`https://www.googletagmanager.com/gtag/js?id=${IDS.ga4}`);
  }
  if (IDS.meta && !window.fbq) {
    const n = (window.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); });
    if (!window._fbq) window._fbq = n;
    n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
    injectScript("https://connect.facebook.net/en_US/fbevents.js");
    window.fbq("init", IDS.meta);
    window.fbq("track", "PageView");
  }
  if (IDS.tiktok && !window.ttq) {
    const ttq = (window.TiktokAnalyticsObject = "ttq", (window.ttq = window.ttq || []));
    ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];
    ttq.setAndDefer = function (t, e) { t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); }; };
    ttq.methods.forEach((m) => ttq.setAndDefer(ttq, m));
    ttq.instance = function (t) { const e = ttq._i[t] || []; ttq.methods.forEach((m) => ttq.setAndDefer(e, m)); return e; };
    ttq.load = function (e, n) {
      ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = "https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._t = ttq._t || {}; ttq._t[e] = +new Date(); ttq._o = ttq._o || {}; ttq._o[e] = n || {};
      injectScript(`https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${e}&lib=ttq`);
    };
    ttq.load(IDS.tiktok);
    ttq.page();
  }
  if (IDS.clarity && !window.clarity) {
    window.clarity = window.clarity || function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
    injectScript(`https://www.clarity.ms/tag/${IDS.clarity}`);
  }
}

export function initAnalytics() {
  captureClickIds();
  loadPixels();
  if (typeof window !== "undefined") window.addEventListener("stagvault:consent", () => { loadPixels(); });
}

export function pixelStatus() {
  return { loaded, ids: { ...IDS } };
}

export function trackPageView(path) {
  if (!loaded) return;
  if (window.gtag && IDS.ga4) window.gtag("event", "page_view", { page_path: path, page_location: window.location.href });
  if (window.fbq) window.fbq("track", "PageView");
  if (window.ttq) window.ttq.page();
}

/**
 * trackEvent("CheckoutStarted", { plan: "lifetime", value: 25, currency: "GBP" })
 * Returns the event_id used across all channels (pass a fixed one for Purchase).
 */
export function trackEvent(name, params = {}, eventId) {
  if (typeof window === "undefined") return null;
  const id = eventId || newEventId(name.toLowerCase());
  const [gaName, metaName, ttName] = EVENT_MAP[name] || [name.toLowerCase(), name, null];
  const { email, ...clean } = params;

  if (loaded) {
    try {
      if (window.gtag && IDS.ga4) window.gtag("event", gaName, { ...clean, transaction_id: clean.transaction_id, event_id: id });
      if (window.fbq) window.fbq(META_STANDARD.has(metaName) ? "track" : "trackCustom", metaName, { ...clean, currency: clean.currency, value: clean.value }, { eventID: id });
      if (window.ttq && ttName) window.ttq.track(ttName, { ...clean, content_type: "product" }, { event_id: id });
    } catch { /* never break the UI for analytics */ }
  }

  api.post("/analytics/event", {
    event: name,
    event_id: id,
    page_url: window.location.href,
    referrer: document.referrer || null,
    email: email || null,
    consent: hasConsent(),
    params: clean,
    visitor_id: visitorId(),
    ...attribution(),
  }).catch(() => {});
  return id;
}
